import { useMutation } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import {
	type EmailOriginationEnum,
	type SubscribeToNewsletterMutation,
	type SubscribeToNewsletterMutationVariables
} from '../../__generated__/graphql-client-types';
import { ERROR_SUBSCRIBE, SUCCESS_SUBSCRIBED } from '../../constants/message';
import { emailInputRegistrationConfig } from '../../helpers/form-validation-helper/form-validation-helper';
import { useNotification } from '../../hooks/notification/notification.hooks';
import { useRecaptcha } from '../../hooks/recaptcha/recaptcha.hooks';
import { SUBSCRIBE_TO_NEWSLETTER } from '../../queries/newsletter/newsletter.queries';
import { StyledButton } from '../buttons';
import { Recaptcha } from '../common-components/recaptcha/recaptcha.component';
import { TextInput } from '../inputs/input/text-input/text-input.component';
import { InputGroup } from '../inputs/input-group/input-group.component';
import AlertOctagonIcon from '../svgs/untitled-ui/alert-octagon.svg';

type FormInputs = {
	email: string;
};

export type EmailSignUpFormProps = {
	origin?: EmailOriginationEnum;
};

export const EmailSignUpForm: FunctionComponent<EmailSignUpFormProps> = ({ origin = 'NONE' }) => {
	const { getToken } = useRecaptcha('newsletter');
	const { notifySuccess, notifyWarning } = useNotification();
	const [subscribeToNewsletter] = useMutation<SubscribeToNewsletterMutation, SubscribeToNewsletterMutationVariables>(
		SUBSCRIBE_TO_NEWSLETTER
	);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<FormInputs>();

	/**
	 * subscribe to the newsletter and display the response. Alert if no email was provided
	 */
	const onSubmit = async (formData: FormInputs) => {
		const token = await getToken();
		subscribeToNewsletter({
			variables: {
				input: {
					email: formData.email,
					origin,
					token
				}
			}
		})
			.then((result) => {
				notifySuccess(result?.data?.subscribeToNewsletter || SUCCESS_SUBSCRIBED);
			})
			.catch((error) => {
				logError(error);
				notifyWarning(ERROR_SUBSCRIBE, 3000);
			})
			.finally(reset);
	};

	return (
		<div>
			<h2 className="font-medium db f5 mt0 mb2">Sign up to receive design ideas and product updates</h2>
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-column" aria-label="newsletter signup form">
				<InputGroup className="w-100">
					<TextInput
						ariaLabel="email"
						placeholder="Enter Email Address"
						autoCapitalize={'off'}
						{...register('email', emailInputRegistrationConfig)}
					/>
					<StyledButton buttonType="submit" automationHook={'email-sign-up'}>
						Subscribe
					</StyledButton>
				</InputGroup>
				{/* Input Group doesn't support form invalid state */}
				{errors.email && (
					<div className={`flex flex-row items-center mt3 f6 theme-error gc1`} data-automation={'email-sign-up-error'}>
						<AlertOctagonIcon className="f5 minw1" />
						{errors.email?.message}
					</div>
				)}
				<Recaptcha />
			</form>
		</div>
	);
};
